import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { pageParam } from "@/lib/search-params";
import { stripSearchParams } from "@tanstack/react-router";
import { CACHE, setEdgeCache } from "@/lib/http-cache";
import { getTagBySlug, getPosts, decodeHtmlEntities, featuredImage, type WPPost, type WPTerm } from "@/lib/wp";
import { SITE, PRIMARY_CATEGORIES } from "@/lib/site";
import { COUNTRIES, resolveCountrySlug } from "@/lib/countries";
import { getFocusedMarketPosts, marketNeedsFocus } from "@/lib/market-focus";
import { EditorialHero as StandardsHero } from "@/components/editorial-page-kit";
import { categoryHeroMeta } from "@/lib/category-view";
import { PAGINATION_ARROW_CLASS, paginationNumberClass, paginationRange } from "@/components/list-pagination";
import {
  Bitcoin,
  Building2,
  Car,
  Cpu,
  House,
  Landmark,
  MapPin,
  Newspaper,
  Rocket,
  Search,
  Shirt,
  ShoppingCart,
  Smartphone,
  Truck,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import { ArticleCard } from "@/components/article-card";
import { ArticleGridSkeleton } from "@/components/article-grid-skeleton";
import { ResponsiveAdSlot } from "@/components/ad-slot";
import { AcademyFollowMore } from "@/components/academy/follow-more";
import { academyLinksForTerms } from "@/lib/academy/related-links";
import { EntityEmptyState, RelatedLinksRow } from "@/components/entity-hub";
import { fastLoad, prefetchAdjacentPages, withDeadline } from "@/lib/fast-load";
import type { SearchSchemaInput } from "@tanstack/react-router";


type CountryData = {
  term: WPTerm | null;
  posts: Awaited<ReturnType<typeof getPosts>> & { failed?: boolean };
};

/**
 * A focus market (Taiwan / Hong Kong) rebuilds its own pagination after
 * filtering out cross-tagged mainland stories. While raw WordPress pages
 * remain, the count keeps one page of headroom — if that page turns out to
 * hold no surviving cards, send the reader to the last page that has content
 * instead of rendering an empty listing.
 */
function redirectPastEmptyPage(country: string, page: number, data: CountryData | null) {
  // Never redirect on a failed fetch: an empty result caused by a transient
  // WordPress error must render the page (and its retry path), not bounce the
  // reader to page 1.
  if (!data || page <= 1 || data.posts.failed || data.posts.items.length > 0) return;
  const last = Math.max(1, data.posts.meta.totalPages ?? 1);
  throw redirect({
    to: "/markets/$country",
    params: { country },
    search: { page: last },
    statusCode: 302,
  });
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  general: Newspaper,
  "e-tailing": ShoppingCart,
  fashion: Shirt,
  food: Utensils,
  finance: Landmark,
  living: House,
  electronics: Smartphone,
  logistics: Truck,
  "real-estate": Building2,
  automotive: Car,
  startups: Rocket,
  telecom: Cpu,
  research: Search,
  crypto: Bitcoin,
};

const countryQuery = (slug: string, page: number) =>
  queryOptions({
    queryKey: ["market-country", slug, page],
    queryFn: async (): Promise<CountryData> => {
      // The listing is the page; the tag term only supplies a display name
      // that `COUNTRIES` already knows. Never let the term lookup decide when
      // the listing is allowed to render.
      // Taiwan and Hong Kong are cross-tagged "china" on the backend; those two
      // desks over-fetch, drop the mainland stories and rebuild their own
      // pagination so a page never renders half-empty.
      const posts = marketNeedsFocus(slug)
        ? await getFocusedMarketPosts(slug, page, 16)
        : await getPosts({ tagSlug: slug, page, perPage: 16 }).catch(
            () => ({ items: [], meta: { total: 0, totalPages: 1 }, failed: true }) as CountryData["posts"],
          );
      const term = await withDeadline(getTagBySlug(slug).catch(() => null), 250);
      return { term: term ?? null, posts };
    },
  });

export const Route = createFileRoute("/markets/$country")({
  validateSearch: (search: { page?: unknown } & SearchSchemaInput) => ({ page: pageParam(search.page) }),
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  // Never 404 on a market URL: alternate spellings redirect to the curated
  // slug, anything else is handled by the generic tag listing.
  beforeLoad: ({ params, search }) => {
    const resolved = resolveCountrySlug(params.country);
    if (resolved && resolved !== params.country) {
      throw redirect({ to: "/markets/$country", params: { country: resolved }, search: { page: search.page }, statusCode: 301 });
    }
    if (!resolved) {
      throw redirect({ to: "/tag/$slug", params: { slug: params.country }, search: { page: search.page }, statusCode: 301 });
    }
  },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ params, deps, context }) => {
    const country = COUNTRIES.find((c) => c.slug === params.country);
    if (!country) throw notFound();
    const options = countryQuery(params.country, deps.page);

    // Same lean-snapshot path as section pages: one small durable read puts
    // real cards and the LCP image in the SSR HTML, instead of a ~94KB raw WP
    // listing row that cannot be read back inside the HTML budget.
    if (typeof window === "undefined") {
      const { snapshotFirst } = await import("@/lib/category-snapshot.server");
      const { value, source } = await snapshotFirst<CountryData>(
        "mkt",
        params.country,
        deps.page,
        async () => ((await context.queryClient.ensureQueryData(options).catch(() => null)) as CountryData | null),
        1800,
      );
      if (value) {
        redirectPastEmptyPage(params.country, deps.page, value);
        context.queryClient.setQueryData(options.queryKey, value);
        await setEdgeCache(source === "fresh" ? CACHE.listing : CACHE.dynamic);
        return { initial: value, page: deps.page, country };
      }
    }

    const { data, cached } = await fastLoad(context.queryClient, options, 1800);
    if (cached && !data) throw notFound();
    redirectPastEmptyPage(params.country, deps.page, data);
    await setEdgeCache(data ? CACHE.listing : CACHE.degraded);
    return { initial: data ?? null, page: deps.page, country };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Market not found" }, { name: "robots", content: "noindex" }] };
    const { initial, page, country } = loaderData;
    const name = country.name;
    const total = initial?.posts.meta.total ?? 0;
    const totalPages = initial?.posts.meta.totalPages ?? 1;
    const baseUrl = `${SITE.url}/markets/${params.country}`;
    const pageUrl = (n: number) => (n > 1 ? `${baseUrl}?page=${n}` : baseUrl);
    const canonical = pageUrl(page);
    const title = page > 1 ? `${name} Retail News - Page ${page} - ${SITE.name}` : `${name} Retail News - ${SITE.name}`;
    const description =
      decodeHtmlEntities(initial?.term?.description || "") ||
      `Latest retail, e-commerce and consumer news from ${name}, curated by ${SITE.name}.`;
    // A degraded/skeleton SSR render (no `initial`) must never emit noindex on
    // the canonical market hub — only a confirmed-empty listing may.
    const hasResults = !initial || total > 0;

    const links: Array<{ rel: string; href: string }> = [{ rel: "canonical", href: canonical }];
    if (page > 1) links.push({ rel: "prev", href: pageUrl(page - 1) });
    if (page < totalPages) links.push({ rel: "next", href: pageUrl(page + 1) });
    const lead = initial?.posts.items?.[0];
    const leadImage = lead ? featuredImage(lead) : null;
    const ogImage = leadImage?.src && /^https:\/\//i.test(leadImage.src) ? leadImage.src : null;
    const itemList = (initial?.posts.items ?? []).slice(0, 10).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE.url}/article/${p.slug}`,
      name: decodeHtmlEntities(p.title.rendered),
    }));
    return {
      meta: [
        { title },
        { name: "description", content: description },
        {
          name: "robots",
          content: !hasResults || page > 1 ? "noindex,follow" : "index,follow,max-image-preview:large,max-snippet:-1",
        },
        { property: "og:site_name", content: SITE.name },
        { property: "og:locale", content: SITE.locale },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        ...(ogImage
          ? [
              { property: "og:image", content: ogImage },
              { name: "twitter:image", content: ogImage },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: SITE.twitter },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            url: canonical,
            description,
            inLanguage: "en",
            isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
            about: { "@type": "Place", name },
            ...(itemList.length
              ? { mainEntity: { "@type": "ItemList", itemListElement: itemList } }
              : {}),
          }),
        },

      ],
    };
  },
  component: CountryHubPage,
  notFoundComponent: () => (
    <div className="container-editorial py-24 text-center">
      <p className="category-eyebrow">Not found</p>
      <h1 className="mt-3 page-title">Market not found</h1>
    </div>
  ),
});

function CountryHubPage() {
  const { initial, page, country } = Route.useLoaderData();
  const params = Route.useParams();
  const { data } = useQuery({ ...countryQuery(params.country, page), initialData: initial ?? undefined });
  const queryClient = useQueryClient();
  prefetchAdjacentPages(queryClient, (n) => countryQuery(params.country, n), page, data?.posts.meta.totalPages ?? 1);

  const posts = data?.posts;
  const items = posts?.items ?? [];

  // Client-side counterpart of `redirectPastEmptyPage`: if the loader degraded
  // (no snapshot in time) and the hydrated query comes back with no cards on a
  // deep page, send the reader to the last page that actually has content.
  const navigate = useNavigate();
  useEffect(() => {
    if (!data || page <= 1 || items.length > 0 || data.posts.failed) return;
    navigate({
      to: "/markets/$country",
      params: { country: params.country },
      search: { page: Math.max(1, data.posts.meta.totalPages ?? 1) },
      replace: true,
    });
  }, [data, page, items.length, navigate, params.country]);

  return (
    <>
      <StandardsHero
        metaSide
        icon={<MapPin size={22} />}
        title={`${country.name} Retail News`}
        lede={`Reporting and analysis on retail, e-commerce, and consumer trends in ${country.name}, updated continuously by our newsroom.`}
        meta={categoryHeroMeta(items, posts?.meta.total, 0)}
      />

      <div className="container-editorial py-6">
        <ResponsiveAdSlot
          baseId="market-top-leaderboard"
          mobile={{ size: "mobile-320x100" }}
          desktop={{ size: "leaderboard-970x90" }}
          priority
        />
      </div>

      <RelatedLinksRow
        heading={`Top categories in ${country.name}`}
        accentHeading
        links={PRIMARY_CATEGORIES.map((c) => ({
          key: c.slug,
          label: c.name,
          to: "/$slug",
          params: { slug: c.slug },
          search: { page: 1, country: country.slug },
          icon: CATEGORY_ICONS[c.slug],
        }))}
      />

      {!data ? (
        <ArticleGridSkeleton />
      ) : items.length === 0 ? (
        <EntityEmptyState
          title={`No stories for ${country.name} yet`}
          description="Check back soon, or browse another market below."
        />
      ) : (
        <>
          <section className="container-editorial pb-10">
            <h2 className="sr-only">Latest {country.name} retail stories</h2>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {items.map((p: WPPost) => (
                <ArticleCard key={p.id} post={p} variant="lead" headingLevel={3} />
              ))}
            </div>
          </section>


          {posts!.meta.totalPages > 1 ? (
            <nav
              aria-label="Pagination"
              className="container-editorial flex flex-wrap items-center justify-center gap-2 pb-16 pt-6 hairline-t"
            >
              {page > 1 ? (
                <Link
                  to="/markets/$country"
                  params={{ country: country.slug }}
                  search={{ page: page - 1 }}
                  className={PAGINATION_ARROW_CLASS}
                >
                  Previous
                </Link>
              ) : (
                <span className={`${PAGINATION_ARROW_CLASS} pointer-events-none opacity-40`}>Previous</span>
              )}
              {paginationRange(page, posts!.meta.totalPages).map((item, i) =>
                item === "gap" ? (
                  <span key={`gap-${i}`} className="px-1 font-mono text-xs text-ink-muted">
                    …
                  </span>
                ) : (
                  <Link
                    key={item}
                    to="/markets/$country"
                    params={{ country: country.slug }}
                    search={{ page: item }}
                    aria-current={item === page ? "page" : undefined}
                    className={paginationNumberClass(item === page)}
                  >
                    {item}
                  </Link>
                ),
              )}
              {page < posts!.meta.totalPages ? (
                <Link
                  to="/markets/$country"
                  params={{ country: country.slug }}
                  search={{ page: page + 1 }}
                  className={PAGINATION_ARROW_CLASS}
                >
                  Next
                </Link>
              ) : (
                <span className={`${PAGINATION_ARROW_CLASS} pointer-events-none opacity-40`}>Next</span>
              )}
            </nav>
          ) : null}

        </>
      )}




      <div className="container-editorial">
        <AcademyFollowMore
          items={academyLinksForTerms([{ slug: params.country, name: country.name }], 4)}
          heading={`Retail Academy: ${country.name}`}
          intro="Free background guides on how retail works in this market."
        />
      </div>

    </>

  );
}
